import { FC } from 'react'
import * as moment from 'moment'
import * as styles from 'components/features/pfm/HomeSettingsModule/HomeSettingsModule.css'
import { City } from 'interfaces/locations'
import { Home } from 'interfaces/property'
import Table, { TableColumnDefinition, TableComponentType } from 'components/molecules/tables/Table'
import TextButton from 'components/atoms/buttons/TextButton'
import { BodyText } from 'components/atoms/Text'
import { DATE_FORMAT_US } from 'constants/datetime'
import { formatTableCurrency } from 'helpers/tableHelpers'
import AbsentValue from 'components/atoms/AbsentValue'
import { Editable, SortingProp } from 'interfaces/table'
import { HouseEditHandler } from 'components/features/pfm/HouseEditHandler/HouseEditHandler'
import { HouseUnlinkHandler } from 'components/features/pfm/HouseUnlinkHandler/HouseUnlinkHandler'
import { HouseDeleteHandler } from 'components/features/pfm/HouseDeleteHandler/HouseDeleteHandler'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { homesArraySelector } from 'selectors/propertySelectors'
import { Guards } from 'interfaces/guards'
import { homesGuard } from 'guards/propertyGuards'
import { connectShadedLoadable } from 'helpers/loadableHelpers'


type HomeSettingsProps = {}

const ADDRESS_COLUMN: TableColumnDefinition<Home, Home> = {
  name: 'address',
  title: 'Address',
  component: (home) => `${ home.address1 } ${ home.address2 }`,
  sortingSelector: ({ address1 }) => address1,
}

const CITY_COLUMN: TableColumnDefinition<Home, City> = {
  name: 'city',
  title: 'City, State',
  component: (city) => `${ city.name }, ${ city.state.code }`,
  propsSelector: ({ city }) => city,
  sortingSelector: ({ city }) => city.name,
}

const VALUE_COLUMN: TableColumnDefinition<Home, number> = {
  name: 'value',
  title: (
    <span>
      Estimated <span className={ styles.visibleOneColumnUp }>Current</span> Value
    </span>
  ),
  component: formatTableCurrency,
  propsSelector: ({ estimated_value }) => estimated_value,
  sortingSelector: ({ estimated_value }) => estimated_value, // SAME_SORTING_SELECTOR does not work here
}

const TypedTable = Table as TableComponentType<Editable<Home>>

const HomeSettingsModuleComponent: FC<HomeSettingsProps> = (props) => {
  const {} = props
  const houses = useStoreSelector(homesArraySelector)

  if (houses.length === 0) {
    return (
      <BodyText className={ styles.emptyState }>
        You have no linked homes.
      </BodyText>
    )
  }

  const EDIT_BUTTON: TableColumnDefinition<Home, Home> = {
    name: 'edit',
    title: '',
    component: (home) => {
      if (home.unlinked_at) {
        return (
          <AbsentValue children={ `Unlinked on ${ moment(home.unlinked_at).format(DATE_FORMAT_US) }` } />
        )
      }

      return (
        <HouseEditHandler homeId={ home.id }>
          <TextButton qaMarker='edit-button'>
            Edit
          </TextButton>
        </HouseEditHandler>
      )
    },
    getColSpan: (home) => home.unlinked_at ? 2 : 1,
  }

  const UNLINK_BUTTON: TableColumnDefinition<Home, Home> = {
    name: 'unlink',
    title: '',
    component: (home) =>
      home.unlinked_at ?
        null :
        (
          <HouseUnlinkHandler homeId={ home.id }>
            <TextButton qaMarker='unlink-button'>
              Unlink
            </TextButton>
          </HouseUnlinkHandler>
        ),
    getColSpan: (home) => home.unlinked_at ? 0 : 1,
  }

  const DELETE_BUTTON: TableColumnDefinition<Home, Home> = {
    name: 'delete',
    title: '',
    component: (home) => (
      <HouseDeleteHandler homeId={ home.id }>
        <TextButton qaMarker='delete-button'>
          Delete
        </TextButton>
      </HouseDeleteHandler>
    ),
  }

  const COLUMNS: TableColumnDefinition<Home, any>[] = [
    ADDRESS_COLUMN,
    CITY_COLUMN,
    VALUE_COLUMN,
    EDIT_BUTTON,
    UNLINK_BUTTON,
    DELETE_BUTTON,
  ]

  const sorting: SortingProp = {
    direction: 'asc',
    columnName: 'address',
  }

  return (
    <section>
      <TypedTable columns={ COLUMNS } rows={ houses } sorting={ sorting } className={ styles.homesTable } />
    </section>
  )
}

const guards: Guards = [
  homesGuard,
]

export const HomeSettingsModule = connectShadedLoadable<{}, {}, {}>(() => () => ({}), undefined, guards)(HomeSettingsModuleComponent)

